import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { baseURL, frontendURL, wsURL, wsURLFallback } from '../config/baseUrl';
import ConnectionStatus from './components/ConnectionStatus';
import ContentRenderer from './components/ContentRenderer';
import FullscreenToggle from './components/FullscreenToggle';
import PendingRegistrationStatus from './components/PendingRegistrationStatus';
import QRCodeRegistration from './components/QRCodeRegistration';
import SmoothLoading from './components/SmoothLoading';
const Preview: React.FC = () => {
  const location = useLocation();
  const { deviceId: paramDeviceId } = useParams<{ deviceId?: string }>();

  // Consolidated socket connection state
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [maxReconnectAttempts] = useState(5); // Reduced to 5 attempts
  const [isConnecting, setIsConnecting] = useState(false);
  const [lastConnectionAttempt, setLastConnectionAttempt] = useState(0);

  // Remove redundant state variables
  const [deviceId, setDeviceId] = useState('');

  // New state for streamlined flow
  const [registrationStatus, setRegistrationStatus] = useState('ready'); // ready, pending, completed
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [deviceStatus, setDeviceStatus] = useState(null);
  const [htmlTemplatePlaybackKey, setHtmlTemplatePlaybackKey] = useState(0);
  const deviceStatusRef = useRef(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [isInitializing, setIsInitializing] = useState(true);

  const heartbeatIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const apiCallInProgressRef = useRef(false);
  const statusCheckInProgressRef = useRef(false);
  const initializationCompletedRef = useRef(false);

  useEffect(() => {
    deviceStatusRef.current = deviceStatus;
  }, [deviceStatus]);

  const generateQRCode = useCallback((deviceId: string) => {
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const qrUrl = `${frontendURL}/add-screen?deviceId=${deviceId}&width=${screenWidth}&height=${screenHeight}`;
    setQrCodeUrl(qrUrl);
  }, []);

  const generateDeviceId = useCallback(async () => {
    if (apiCallInProgressRef.current) {
      console.log('🔧 API call already in progress, skipping...');
      return;
    }

    try {
      apiCallInProgressRef.current = true;
      setIsInitializing(true);
      console.log('🔧 Requesting 4-digit device ID from API...');

      const response = await fetch(
        `${baseURL}/api/v1/device-registration/initiateDeviceRegistration`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (data.success && data.data.deviceId) {
        const generatedDeviceId = data.data.deviceId;

        console.log(
          '🔧 Generated 4-digit Device ID from API:',
          generatedDeviceId
        );

        // Set device ID but don't save to localStorage yet
        setDeviceId(generatedDeviceId);

        // Generate QR code URL
        generateQRCode(generatedDeviceId);

        // Only check device status, don't connect WebSocket or start reconnection here
        console.log(
          '🔍 Checking device status for generated ID:',
          generatedDeviceId
        );
        checkDeviceStatus(generatedDeviceId);
      } else {
        throw new Error(data.error?.message || 'Failed to generate device ID');
      }
    } catch (error) {
      console.error('Error generating device ID from API:', error);
      setError('Device ID generation failed. Please try again.');
      setIsInitializing(false);
    } finally {
      apiCallInProgressRef.current = false;
    }
  }, [generateQRCode]);

  const checkDeviceStatus = useCallback(async (deviceId: string) => {
    if (statusCheckInProgressRef.current) {
      console.log('🔍 Device status check already in progress, skipping...');
      return undefined;
    }

    try {
      statusCheckInProgressRef.current = true;
      console.log('Checking device status for:', deviceId);

      const response = await fetch(
        `${baseURL}/api/v1/device-registration/device/${deviceId}/status`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      const data = await response.json();

      if (response.status === 402) {
        setRegistrationStatus('locked');
        setError(null);
        setMessage(
          data?.error?.message || 'Subscription is not active'
        );
        return null;
      }

      if (data.success) {
        const deviceData = data.data;
        setDeviceStatus(deviceData);
        setError(null);

        if (deviceData.isRegistered) {
          if (deviceData.contentType && deviceData.content) {
            setRegistrationStatus('completed');
            setMessage('Device is displaying content!');
          } else {
            setRegistrationStatus('pending');
            setMessage(
              'Device registered successfully! Waiting for content assignment...'
            );
          }
        } else if (deviceData.status === 'pending') {
          setRegistrationStatus('pending');
          setMessage('Device is pending registration.');
        } else {
          setRegistrationStatus('ready');
          setMessage(
            'Device ready for registration. Scan QR code to start the process.'
          );
        }
        return deviceData;
      } else {
        setDeviceStatus(null);
        setRegistrationStatus('ready');
        setMessage(
          'Device ready for registration. Scan QR code to start the process.'
        );
        setError(null);
        return null;
      }
    } catch (error) {
      console.error('Error checking device status:', error);
      setError('Failed to connect to server');
      setDeviceStatus(null);
      return null;
    } finally {
      setIsInitializing(false);
      statusCheckInProgressRef.current = false;
    }
  }, []);

  const connectWebSocket = useCallback(
    (deviceId: string) => {
      if (isConnecting || (socket && socket.connected)) {
        console.log('🔌 Connection already in progress or established');
        return;
      }

      const now = Date.now();
      if (now - lastConnectionAttempt < 5000) {
        console.log('🔌 Connection rate limited, waiting...');
        return;
      }

      try {
        console.log('🔌 Attempting to connect to WebSocket...');
        setIsConnecting(true);
        setLastConnectionAttempt(now);

        // Clean up existing socket if any
        if (socket) {
          console.log('🔌 Cleaning up existing socket connection');
          socket.disconnect();
          setSocket(null);
          setIsConnected(false);
        }

        console.log('🔌 Attempting to connect to WebSocket URL:', wsURL);

        const newSocket = io(wsURL, {
          transports: ['polling', 'websocket'], // Start with polling, then upgrade to websocket
          autoConnect: true,
          timeout: 15000,
          forceNew: true,
          reconnection: false, // Disable automatic reconnection to prevent loops
          upgrade: true,
          rememberUpgrade: true,
        });

        // Connection event handlers
        newSocket.on('connect', () => {
          console.log('🔌 WebSocket connected successfully');
          console.log('🔌 Socket ID:', newSocket.id);
          setIsConnecting(false);
          setIsConnected(true);
          setReconnectAttempts(0);

          // Join device room
          newSocket.emit('join-device-room', deviceId);
          console.log(`🔌 Emitted join-device-room for device: ${deviceId}`);

          // Stop any reconnection loop
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
            console.log(
              '🔄 Reconnection loop stopped after successful connection'
            );
          }

          // Don't save device ID to localStorage on connection
          // It will be saved only when device is verified by admin

          // Send immediate heartbeat to mark device as online
          setTimeout(() => {
            if (newSocket.connected) {
              console.log('💓 Sending immediate heartbeat after connection...');
              newSocket.emit('heartbeat', {
                deviceId,
                timestamp: Date.now(),
                source: 'connection-establishment',
              });
            }
          }, 1000);
        });

        newSocket.on('connect_error', (error: any) => {
          console.error('🔌 WebSocket connection error:', error);
          console.error('🔌 Error details:', {
            message: error.message,
            type: error.type || 'unknown',
            description: error.description || 'no description',
            context: error.context || 'no context',
            url: wsURL,
            environment: process.env.NODE_ENV,
            hostname: window.location.hostname,
          });

          setIsConnecting(false);
          setIsConnected(false);

          // Try fallback URL if main URL fails
          if (wsURLFallback !== wsURL) {
            console.log('🔌 Trying fallback URL...');
            setTimeout(() => {
              connectWebSocketFallback(deviceId);
            }, 1000);
          } else {
            // Start reconnection loop only if no fallback available
            setTimeout(() => {
              startReconnectionLoop(deviceId);
            }, 1000);
          }
        });

        // Room management
        newSocket.on('room-joined', data => {
          console.log(
            `🔌 Successfully joined room: ${data.roomId} for device: ${data.deviceId}`
          );
        });

        newSocket.on('join-device-room', data => {
          console.log('🔌 Joined device room:', data);
          // Device status is already checked during initialization
        });

        // Heartbeat handling
        newSocket.on('heartbeat-ack', data => {
          console.log('🔌 Heartbeat acknowledged:', data.timestamp);
        });

        // Disconnection handling
        newSocket.on('disconnect', reason => {
          console.log('🔌 WebSocket disconnected:', reason);
          setIsConnected(false);

          // Only start reconnection for unexpected disconnections
          if (reason !== 'io client disconnect') {
            console.log(
              '🔄 Unexpected disconnection, starting reconnection...'
            );
            setTimeout(() => {
              startReconnectionLoop(deviceId);
            }, 1000);
          }
        });

        // Content events
        newSocket.on('content-assigned', data => {
          console.log('🔌 Content assigned event received:', data);
          if (data.deviceId === deviceId) {
            setRegistrationStatus('completed');
            setMessage('Content assigned successfully! Displaying content...');
            setTimeout(() => checkDeviceStatus(deviceId), 1000);
          }
        });

        newSocket.on('content-changed', data => {
          console.log('🔌 Content changed event received:', data);
          if (
            data.deviceId === deviceId ||
            data.deviceId === deviceStatus?.deviceId
          ) {
            setMessage('Content changed! Refreshing device status...');
            setTimeout(() => {
              const targetDeviceId =
                data.deviceId || deviceId || deviceStatus?.deviceId;
              if (targetDeviceId) checkDeviceStatus(targetDeviceId);
            }, 1000);
          }
        });

        newSocket.on('refresh-device', data => {
          console.log('🔌 Refresh device event received:', data);
          const targetId = data?.deviceId;
          if (!targetId) return;
          if (targetId !== deviceId && targetId !== deviceStatus?.deviceId) {
            return;
          }
          setMessage('Refreshing display...');
          setTimeout(async () => {
            const targetDeviceId =
              targetId || deviceId || deviceStatusRef.current?.deviceId;
            if (!targetDeviceId) return;
            if (deviceStatusRef.current?.contentType === 'html_template') {
              setHtmlTemplatePlaybackKey(k => k + 1);
            }
            const next = await checkDeviceStatus(targetDeviceId);
            if (next?.contentType === 'html_template') {
              setHtmlTemplatePlaybackKey(k => k + 1);
            }
          }, 1000);
        });

        newSocket.on('device-verified', data => {
          console.log('🔌 Device verified event received:', data);
          if (
            data.deviceId === deviceId ||
            data.deviceId === deviceStatus?.deviceId
          ) {
            setMessage('Device verified! Refreshing device status...');
            setTimeout(() => {
              const targetDeviceId = data.deviceId || deviceId || deviceStatus?.deviceId;
              if (targetDeviceId) checkDeviceStatus(targetDeviceId);
            }, 1000);
          }
        });

        newSocket.on('device-registered', data => {
          console.log('🔌 Device registered event received:', data);
          if (data.deviceId === deviceId) {
            // Save device ID to localStorage when device is registered
            if (data.deviceId && !localStorage.getItem('deviceCode')) {
              localStorage.setItem('deviceCode', data.deviceId);
              console.log(
                '💾 Device ID saved to localStorage after device registration:',
                data.deviceId
              );
            }
            
            setRegistrationStatus('pending');
            setMessage(
              'Device registered successfully! Waiting for content assignment...'
            );
            setTimeout(() => checkDeviceStatus(deviceId), 1000);
          }
        });

        newSocket.on('device-status-updated', data => {
          console.log('🔌 Device status updated event received:', data);
          if (
            data.deviceId === deviceId ||
            data.deviceId === deviceStatus?.deviceId
          ) {
            if (deviceStatus) {
              setDeviceStatus(prev => ({ ...prev, isOnline: data.isOnline }));
            }
          }
        });

        newSocket.on('device-deleted', data => {
          console.log('🔌 Device deleted event received:', data);
          if (
            data.deviceId === deviceId ||
            data.deviceId === deviceStatus?.deviceId
          ) {
            setMessage('Device has been deleted from the admin panel');
            setDeviceStatus(null);
          }
        });

        setSocket(newSocket);

        // Connection timeout
        setTimeout(() => {
          if (!newSocket.connected && !isConnected) {
            console.log('🔌 WebSocket connection timeout');
            setIsConnecting(false);
            setTimeout(() => {
              startReconnectionLoop(deviceId);
            }, 1000);
          }
        }, 15000);
      } catch (error) {
        console.error('Error connecting to WebSocket:', error);
        setIsConnecting(false);
        setIsConnected(false);
        setTimeout(() => {
          startReconnectionLoop(deviceId);
        }, 1000);
      }
    },
    [
      wsURL,
      wsURLFallback,
      isConnecting,
      socket,
      isConnected,
      deviceStatus?.deviceId,
      checkDeviceStatus,
      reconnectTimeoutRef,
      lastConnectionAttempt,
    ]
  );

  // Fallback connection function
  const connectWebSocketFallback = useCallback(
    (deviceId: string) => {
      if (isConnecting) return;

      try {
        console.log('🔌 Attempting fallback connection to:', wsURLFallback);
        setIsConnecting(true);
        setLastConnectionAttempt(Date.now());

        if (socket) {
          socket.disconnect();
          setSocket(null);
          setIsConnected(false);
        }

        const fallbackSocket = io(wsURLFallback, {
          transports: ['websocket', 'polling'],
          autoConnect: true,
          timeout: 15000,
          forceNew: true,
          reconnection: false,
          upgrade: true,
          rememberUpgrade: true,
        });

        fallbackSocket.on('connect', () => {
          console.log('🔌 Fallback WebSocket connected successfully');
          setIsConnecting(false);
          setIsConnected(true);
          setReconnectAttempts(0);
          fallbackSocket.emit('join-device-room', deviceId);

          // Stop reconnection loop
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
          }

          // Don't save device ID to localStorage on fallback connection
          // It will be saved only when device is verified by admin

          setSocket(fallbackSocket);
        });

        fallbackSocket.on('connect_error', () => {
          console.log('🔌 Fallback connection also failed');
          setIsConnecting(false);
          setIsConnected(false);
          setTimeout(() => {
            startReconnectionLoop(deviceId);
          }, 1000);
        });

        // Add other event handlers similar to main connection
        // ... (same event handlers as above)
      } catch (error) {
        console.error('Error connecting to fallback WebSocket:', error);
        setIsConnecting(false);
        setIsConnected(false);
        setTimeout(() => {
          startReconnectionLoop(deviceId);
        }, 1000);
      }
    },
    [wsURLFallback, isConnecting, socket, reconnectTimeoutRef]
  );

  const startReconnectionLoop = useCallback(
    (deviceId: string) => {
      // Prevent multiple reconnection loops
      if (reconnectTimeoutRef.current || isConnecting) {
        console.log(
          '🔄 Reconnection loop already running or connection in progress'
        );
        return;
      }

      // Prevent reconnection if max attempts reached
      if (reconnectAttempts >= maxReconnectAttempts) {
        console.log('❌ Max reconnection attempts reached, stopping loop');
        return;
      }

      console.log('🔄 Starting reconnection loop...');
      const currentAttempts = reconnectAttempts + 1;
      setReconnectAttempts(currentAttempts);

      console.log(
        `🔄 Reconnection attempt ${currentAttempts}/${maxReconnectAttempts}`
      );

      // Use timeout instead of interval for single reconnection attempt
      reconnectTimeoutRef.current = setTimeout(() => {
        if (!isConnected && !isConnecting) {
          console.log(
            `🔄 Attempting reconnection ${currentAttempts}/${maxReconnectAttempts}`
          );
          connectWebSocket(deviceId);
        }
        reconnectTimeoutRef.current = null;
      }, 10000); // Wait 10 seconds before reconnection attempt
    },
    [
      reconnectAttempts,
      maxReconnectAttempts,
      isConnected,
      isConnecting,
      connectWebSocket,
    ]
  );

  // Main initialization effect - only run once
  useEffect(() => {
    if (initializationCompletedRef.current) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const queryDeviceId = searchParams.get('d');

    if (queryDeviceId) {
      setDeviceId(queryDeviceId);
      checkDeviceStatus(queryDeviceId);
    } else {
      const storedDeviceId = localStorage.getItem('deviceCode');
      if (storedDeviceId) {
        setDeviceId(storedDeviceId);
        checkDeviceStatus(storedDeviceId);
      } else {
        generateDeviceId().catch(error => {});
      }
    }

    initializationCompletedRef.current = true;

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (socket) {
        socket.disconnect();
      }
    };
  }, [location.search]);

  // Connect WebSocket only when device status is available and not already connected
  useEffect(() => {
    if (deviceStatus?.deviceId && !isConnected && !socket && !isConnecting) {
      console.log('🔌 Device ID available, connecting to WebSocket...');
      console.log('🔌 WebSocket URL:', wsURL);
      console.log('🔌 Fallback URL:', wsURLFallback);
      connectWebSocket(deviceStatus.deviceId);
    }
  }, [
    deviceStatus?.deviceId,
    isConnected,
    socket,
    isConnecting,
  ]);

  // Handle visibility change and page unload
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && deviceStatus?.deviceId && socket) {
        console.log('🔌 Preview page hidden/closed, setting device offline...');
        socket.emit('device-offline', { deviceId: deviceStatus.deviceId });
      }
    };

    const handleBeforeUnload = () => {
      if (deviceStatus?.deviceId) {
        console.log('🔌 Preview page closing, setting device offline...');
        navigator.sendBeacon(
          `${baseURL}/api/v1/devices/${deviceStatus.deviceId}/offline`,
          JSON.stringify({ deviceId: deviceStatus.deviceId })
        );
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [deviceStatus?.deviceId, socket, baseURL]);

  // Heartbeat effect - only when connected
  useEffect(() => {
    if (deviceStatus?.deviceId && isConnected && socket) {
      console.log('💓 Starting heartbeat for device:', deviceStatus.deviceId);

      const heartbeatInterval = setInterval(() => {
        if (socket && socket.connected) {
          console.log('💓 Sending heartbeat...');

          // Send WebSocket heartbeat
          socket.emit('heartbeat', {
            deviceId: deviceStatus.deviceId,
            timestamp: Date.now(),
            source: 'preview-screen',
          });

          // Also send HTTP heartbeat as backup
          fetch(
            `${baseURL}/api/v1/devices/${deviceStatus.deviceId}/heartbeat`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                deviceId: deviceStatus.deviceId,
                timestamp: Date.now(),
                source: 'preview-screen',
              }),
            }
          )
            .then(response => {
              if (response.ok) {
                console.log('💓 HTTP heartbeat sent successfully');
              } else {
                console.error('💓 HTTP heartbeat failed:', response.status);
              }
            })
            .catch(error => {
              console.error('Error sending HTTP heartbeat:', error);
            });
        } else {
          console.log('💓 Socket not connected, skipping heartbeat');
        }
      }, 60000); // Send heartbeat every 60 seconds (1 minute)

      heartbeatIntervalRef.current = heartbeatInterval;

      return () => {
        clearInterval(heartbeatInterval);
        console.log('💓 Stopped heartbeat for device:', deviceStatus.deviceId);
      };
    }
  }, [deviceStatus?.deviceId, isConnected, socket]);

  // Generate QR code when device ID is available
  useEffect(() => {
    if (deviceId && !qrCodeUrl) {
      generateQRCode(deviceId);
    }
  }, [deviceId, qrCodeUrl]);

  useEffect(() => {
    if (!deviceId) return;
    const timer = window.setInterval(() => {
      checkDeviceStatus(deviceId);
    }, 60000);
    return () => window.clearInterval(timer);
  }, [deviceId, checkDeviceStatus]);

  const renderContent = () => {
    if (deviceStatus?.content) {
      return (
        <ContentRenderer
          deviceStatus={deviceStatus}
          htmlTemplatePlaybackKey={htmlTemplatePlaybackKey}
        />
      );
    }
  };

  // Show smooth loading during initialization
  if (isInitializing) {
    return (
      <SmoothLoading
        message="Setting up your display..."
        subMessage="Please wait while we connect to the server"
      />
    );
  }

  if (registrationStatus === 'locked') {
    return (
      <div className="w-screen h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center max-w-xl px-8">
          <h1 className="text-4xl font-semibold mb-4">Subscription inactive</h1>
          <p className="text-xl text-white/80">
            This screen is paused until the subscription is active again.
          </p>
        </div>
      </div>
    );
  }

  // Show error state if there's an error
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-screen h-screen bg-black relative overflow-hidden">
      <ConnectionStatus isConnected={isConnected} />
      <FullscreenToggle />

      {/* Message Display */}
      {/* {message && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-blue-500/20 backdrop-blur-sm text-blue-400 px-4 py-2 rounded-lg border border-blue-500/30">
            <span className="text-sm font-medium">{message}</span>
          </div>
        </div>
      )} */}

      {(!deviceStatus || registrationStatus === 'ready') && (
        <QRCodeRegistration
          qrCodeUrl={qrCodeUrl}
          deviceId={deviceId}
          onUrlCopy={message => {
            setMessage(message);
            setTimeout(() => setMessage(''), 2000);
          }}
          screenHeight={window.screen.height}
          screenWidth={window.screen.width}
        />
      )}

      {registrationStatus === 'pending' && (
        <PendingRegistrationStatus message={message} />
      )}

      {/* Content Display */}
      {registrationStatus === 'completed' && (
        <div className="w-full h-full">{renderContent()}</div>
      )}
    </div>
  );
};

export default Preview;
